// src/cockpit4/components/AutoIncomePanel.tsx
// ============================================
// 自动收入面板（AI 自主赚钱飞轮 · 第 4 环 变现层）
//
// 功能：
//   - 记录各平台自动收益（公众号流量主/抖音中视频/小红书/知乎/带货/服务等）
//   - 汇总：本月收入 / 今日 / 近 7 天 / 平台分布
//   - 近 30 天收入趋势图
//   - localStorage 持久化（xeaos_auto_income）
//
// 定位：让"系统赚钱"可视化——飞轮闭环从"会干活"升级到"会算钱"
// ============================================

import { useState, useEffect, useMemo, useCallback } from "react";
import { DollarSign, Plus, Trash2, TrendingUp, Wallet, CalendarDays, Loader2 } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

// ============================================
// 类型与常量
// ============================================

interface IncomeRecord {
  id: string;
  date: string;        // YYYY-MM-DD
  platform: string;    // 平台/来源
  amount: number;      // 金额（元）
  note: string;        // 说明（可选）
  createdAt: string;
}

const PLATFORMS = ["微信公众号", "抖音", "小红书", "知乎", "B站", "带货佣金", "企业服务", "其他"];

const PLATFORM_COLORS: Record<string, string> = {
  微信公众号: "#07C160",
  抖音: "#000000",
  小红书: "#FF2442",
  知乎: "#0084FF",
  B站: "#FB7299",
  带货佣金: "#F59E0B",
  企业服务: "#8B5CF6",
  其他: "#9CA3AF",
};

const STORAGE_KEY = "xeaos_auto_income";

// ============================================
// 主组件
// ============================================

export default function AutoIncomePanel() {
  const [records, setRecords] = useState<IncomeRecord[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as IncomeRecord[];
    } catch { /* 忽略 */ }
    return [];
  });

  // 录入表单
  const [platform, setPlatform] = useState("微信公众号");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);

  // 持久化
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(records)); } catch { /* 忽略 */ }
  }, [records]);

  const today = new Date().toISOString().slice(0, 10);

  // 本月 / 今日 / 近 7 天 统计
  const stats = useMemo(() => {
    const monthPrefix = today.slice(0, 7);
    const month = records.filter((r) => r.date.startsWith(monthPrefix)).reduce((s, r) => s + r.amount, 0);
    const day = records.filter((r) => r.date === today).reduce((s, r) => s + r.amount, 0);
    const last7 = records.filter((r) => {
      const diff = (Date.now() - new Date(r.date).getTime()) / 86400000;
      return diff >= 0 && diff <= 7;
    }).reduce((s, r) => s + r.amount, 0);
    return { month, day, last7, total: records.reduce((s, r) => s + r.amount, 0) };
  }, [records, today]);

  // 平台分布
  const byPlatform = useMemo(() => {
    const map: Record<string, number> = {};
    records.forEach((r) => { map[r.platform] = (map[r.platform] || 0) + r.amount; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [records]);

  // 近 30 天趋势
  const trend = useMemo(() => {
    const days: { date: string; 收入: number }[] = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const sum = records.filter((r) => r.date === key).reduce((s, r) => s + r.amount, 0);
      days.push({ date: key.slice(5), 收入: sum });
    }
    return days;
  }, [records]);

  // 最近记录（倒序）
  const recent = useMemo(
    () => [...records].sort((a, b) => (b.date > a.date ? 1 : -1)).slice(0, 30),
    [records]
  );

  const handleAdd = useCallback(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || !date) return;
    setAdding(true);
    setTimeout(() => {
      const rec: IncomeRecord = {
        id: `inc-${Date.now()}`,
        date,
        platform,
        amount: Math.round(amt * 100) / 100,
        note: note.trim(),
        createdAt: new Date().toISOString(),
      };
      setRecords((prev) => [...prev, rec]);
      setAmount("");
      setNote("");
      setAdding(false);
    }, 200);
  }, [amount, date, platform, note]);

  const handleDelete = useCallback((id: string) => {
    setRecords((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const fmt = (n: number) => `¥${n.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="h-full w-full overflow-y-auto p-4">
      <div className="space-y-4 max-w-5xl mx-auto">
        {/* ============ 标题栏 ============ */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <DollarSign size={16} className="text-white" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-white">自动收入 · 变现层</h3>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                AI 自主赚钱飞轮 · 第 4 环 · 让系统"会算钱"
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-gray-600">数据本地持久化</span>
        </div>

        {/* ============ KPI 卡片 ============ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {([
            { label: "本月收入", value: stats.month, icon: Wallet, color: "#34D399" },
            { label: "今日收入", value: stats.day, icon: DollarSign, color: "#60A5FA" },
            { label: "近 7 天", value: stats.last7, icon: TrendingUp, color: "#FBBF24" },
            { label: "累计收入", value: stats.total, icon: CalendarDays, color: "#A78BFA" },
          ] as const).map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={13} style={{ color: k.color }} />
                  <span className="text-[10px] font-mono text-gray-500">{k.label}</span>
                </div>
                <div className="text-[18px] font-semibold" style={{ color: k.color }}>
                  {fmt(k.value)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-12 gap-3">
          {/* ============ 左：录入 + 平台分布 ============ */}
          <div className="col-span-5 space-y-3">
            {/* 录入表单 */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
              <div className="text-[11px] font-medium text-white mb-3 flex items-center gap-1.5">
                <Plus size={12} className="text-emerald-400" /> 记录一笔收益
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    className="px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none focus:border-emerald-500/40"
                  >
                    {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none focus:border-emerald-500/40"
                  />
                </div>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="金额（元）"
                  className="w-full px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none focus:border-emerald-500/40"
                />
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="说明（可选，如：流量主结算 / 广告费）"
                  className="w-full px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none focus:border-emerald-500/40"
                />
                <button
                  onClick={handleAdd}
                  disabled={adding || !amount || parseFloat(amount) <= 0}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                >
                  {adding ? <Loader2 size={12} className="animate-spin text-emerald-400" /> : <Plus size={12} className="text-emerald-400" />}
                  <span className="text-[11px] font-medium text-emerald-300">入账</span>
                </button>
              </div>
            </div>

            {/* 平台分布 */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
              <div className="text-[11px] font-medium text-white mb-3">平台收入分布</div>
              {byPlatform.length === 0 ? (
                <p className="text-[10px] text-gray-600 italic">暂无收益记录，录入后自动统计</p>
              ) : (
                <div className="space-y-2">
                  {byPlatform.map(([p, sum]) => {
                    const pct = stats.total > 0 ? Math.round((sum / stats.total) * 100) : 0;
                    const color = PLATFORM_COLORS[p] || "#9CA3AF";
                    return (
                      <div key={p}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] font-mono" style={{ color }}>
                            {p}
                          </span>
                          <span className="text-[10px] font-mono text-gray-400">{fmt(sum)} · {pct}%</span>
                        </div>
                        <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ============ 右：趋势 + 最近记录 ============ */}
          <div className="col-span-7 space-y-3">
            {/* 30 天趋势 */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
              <div className="text-[11px] font-medium text-white mb-2">近 30 天收入趋势</div>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34D399" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 9 }} tickLine={false} axisLine={false} interval={4} />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 9 }} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{ background: "#0d1220", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
                      labelStyle={{ color: "#9CA3AF" }}
                      formatter={(v: any) => [fmt(Number(v)), "收入"]}
                    />
                    <Area type="monotone" dataKey="收入" stroke="#34D399" strokeWidth={1.5} fill="url(#incomeGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 最近记录 */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-medium text-white">最近收益记录（{recent.length}）</span>
                {records.length > 0 && (
                  <button
                    onClick={() => { if (confirm("确认清空全部收益记录？")) setRecords([]); }}
                    className="text-[9px] font-mono text-gray-500 hover:text-red-400 transition-colors"
                  >
                    清空
                  </button>
                )}
              </div>
              {recent.length === 0 ? (
                <p className="text-[10px] text-gray-600 italic py-2">
                  暂无记录。公众号流量主/抖音中视频等收益到账后，在这里入账，飞轮就能看见钱
                </p>
              ) : (
                <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                  {recent.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/[0.03] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                    >
                      <span
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: `${PLATFORM_COLORS[r.platform] || "#9CA3AF"}20`, color: PLATFORM_COLORS[r.platform] || "#9CA3AF" }}
                      >
                        {r.platform}
                      </span>
                      <span className="text-[10px] font-mono text-gray-500 flex-shrink-0">{r.date}</span>
                      <span className="text-[10px] text-gray-300 truncate flex-1">{r.note || r.platform}</span>
                      <span className="text-[11px] font-mono text-emerald-300 flex-shrink-0">{fmt(r.amount)}</span>
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-0.5 rounded hover:bg-red-500/20 text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                        title="删除"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
